/**
 * The whole of signing in: one field, and the page a link lands on.
 *
 * THE TOKEN ARRIVES IN THE URL FRAGMENT AND IS SPENT FROM SCRIPT. Both halves matter. A
 * fragment is never sent to a server, so the token stays out of access logs, out of
 * `Referer`, and out of every proxy in between. And spending it with a POST from here
 * means the corporate mail scanner that fetches every link in every email before
 * delivering it gets this HTML and does not spend the token - a GET endpoint that signs
 * you in is a GET endpoint Outlook signs you in from at 3am, leaving the human with a
 * link that has already been used.
 *
 * THE FORM ALWAYS SAYS THE SAME THING. "If that address has an account, a link is on its
 * way" - never "no such user", because the endpoint deliberately cannot tell them apart
 * and a form that pretended otherwise would give away what the API refuses to.
 */

import { useEffect, useState } from "react";
import { ApiError, post } from "../api";
import { Mark } from "../components/Icons";
import { Button, TextInput } from "../ui";
import { useSession } from "../session";

type Stage = "form" | "sending" | "sent" | "verifying";

/*
 * The mark is large here and nowhere else in the product. This is the one moment the
 * application gets to say what it is before it starts showing numbers, and a login form
 * with a 16px logo above it says nothing at all.
 */
const SHELL = "grid min-h-screen place-items-center bg-ground p-6";
const CARD =
  "w-full max-w-[380px] rounded-xl border border-line bg-paper px-7.5 pt-7.5 pb-6.5";

export function SignIn() {
  const { reload } = useSession();
  // Filled in from `?email=`, which is where an invitation lands: the person only has to press
  // the button. An address, not a credential - the link is still minted by that press.
  const [email, setEmail] = useState(
    () => new URLSearchParams(window.location.search).get("email")?.trim() ?? "",
  );
  const [stage, setStage] = useState<Stage>("form");
  const [error, setError] = useState<string | null>(null);
  // Development only. With no mail provider configured the API returns the link rather
  // than sending it, which is what makes `pnpm dev` a real sign-in - the same tokens and
  // the same cookie - instead of a bypass in the Worker's middleware.
  const [devLink, setDevLink] = useState<string | null>(null);

  // The link landing. Runs once, before anything is rendered that could distract from it.
  useEffect(() => {
    const token = new URLSearchParams(window.location.hash.replace(/^#/, "")).get("token");
    if (!token) return;

    setStage("verifying");
    // Cleared from the address bar immediately, so a spent token is not sitting in the
    // history of a shared machine.
    window.history.replaceState(null, "", window.location.pathname);

    post("/v1/auth/verify", { token })
      .then(() => reload())
      .catch((err: unknown) => {
        setStage("form");
        setError(err instanceof ApiError ? err.message : "That link did not work");
      });
  }, [reload]);

  async function request(event: React.FormEvent) {
    event.preventDefault();
    setStage("sending");
    setError(null);
    try {
      const answer = await post<{ sent: boolean; link?: string }>("/v1/auth/request", { email });
      setDevLink(answer.link ?? null);
      setStage("sent");
    } catch (err) {
      setStage("form");
      setError(err instanceof ApiError ? err.message : "Could not send that");
    }
  }

  if (stage === "verifying") {
    return (
      <main className={SHELL}>
        <div className={CARD}>
          <Mark size={40} className="mb-4.5 text-accent" />
          <h1 className="mb-1 text-xl font-[640] tracking-[-0.025em]">Voice Agents</h1>
          <p className="text-sm text-ink-soft">Signing you in…</p>
        </div>
      </main>
    );
  }

  return (
    <main className={SHELL}>
      <div className={CARD}>
        <Mark size={40} className="mb-4.5 text-accent" />
        <h1 className="mb-1 text-xl font-[640] tracking-[-0.025em]">Voice Agents</h1>
        <p className="mb-5.5 text-sm leading-normal text-ink-soft">
          What your phone did while you were busy.
        </p>

        {stage === "sent" ? (
          <>
            <p className="rounded-sm border border-good-line bg-good-soft px-3.25 py-2.75 text-sm leading-normal text-good">
              If <strong>{email}</strong> has an account, a sign-in link is on its way. It works
              once and expires in fifteen minutes.
            </p>
            {devLink && (
              <p className="mt-3 rounded-sm border border-warm-line bg-warm-soft px-3 py-2.5 text-xs break-words text-warm">
                No mail provider is configured, so here it is:{" "}
                <a className="font-semibold underline" href={devLink}>
                  sign in as {email}
                </a>
              </p>
            )}
            <button
              className="mt-3.5 cursor-pointer text-sm font-medium text-accent hover:underline"
              onClick={() => setStage("form")}
            >
              Use a different address
            </button>
          </>
        ) : (
          <form className="grid gap-[8px]" onSubmit={request}>
            <label className="text-xs font-[550] text-ink-soft" htmlFor="email">
              Your email address
            </label>
            <TextInput
              id="email"
              type="email"
              autoComplete="email"
              required
              autoFocus
              className="px-2.75 py-2.25"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@yourbusiness.com"
            />
            <Button
              variant="primary"
              type="submit"
              className="mt-0.5 justify-center py-2.25"
              disabled={stage === "sending"}
            >
              {stage === "sending" ? "Sending…" : "Email me a link"}
            </Button>
            {/* No password field, and nothing to remember or reset. */}
            <p className="mt-2 text-sm leading-normal text-ink-soft">
              We will email you a link. There is no password.
            </p>
          </form>
        )}

        {error && <p className="mt-3 text-sm text-alert">{error}</p>}
      </div>
    </main>
  );
}
